import {
  ContentFilterValue,
  ContentFilterDataItem,
  FilterObject,
  FilterState,
  FilterValueObject,
  LINK_FILTER,
  LINK_FILTER_TYPE,
  MULTISELECT_FILTER,
  MULTISELECT_FILTER_TYPE,
  ActiveFilters,
  ContentFilterSortingOption,
  ContentFilterDisplayVariant,
} from '@as-react/common-types';

export const normalizeContentFilter = (
  contentFilterValues: Array<ContentFilterValue>,
  filterType: string,
  queryParamKey: string,
  nameTranslationKey: string,
  sortingType: number = 1,
  displayVariant: number,
  activeValues?: Array<string>
): FilterObject => ({
  filterOn: contentFilterValues[0]!.value.split(':')[0]!,
  name: nameTranslationKey,
  type: filterType,
  values: contentFilterValues
    .map(
      (filterValue: ContentFilterValue): FilterValueObject => ({
        name: filterValue.label,
        filterKey: filterValue.value.split(':')[1] ?? '',
        itemCount: filterValue.count,
        active: !!activeValues && activeValues.length > 0 ? activeValues.indexOf(filterValue.value) !== -1 : false,
      })
    )
    .sort((a: FilterValueObject, b: FilterValueObject) =>
      sortingType === ContentFilterSortingOption.ALPHABETICAL
        ? a.name.toLowerCase() > b.name.toLowerCase()
          ? 1
          : -1
        : b.itemCount! - a.itemCount!
    ),
  filterContentType: null,
  queryParamKey: queryParamKey,
  toggleFilterOptions: displayVariant === ContentFilterDisplayVariant.HIDE_EXTRA_ITEMS,
});

export const mergeContentFilterData = (
  availableFilterValues: Array<ContentFilterDataItem>,
  filterValues: Array<ContentFilterValue>
) => {
  return availableFilterValues
    .map((availableFilterValue: ContentFilterDataItem) => {
      const filterValue = filterValues.find(
        (filterValue: ContentFilterValue) => filterValue.value === availableFilterValue.value
      );

      return filterValue
        ? {
            ...filterValue,
            count: availableFilterValue.count,
          }
        : null;
    })
    .filter((filterValue: ContentFilterValue | null) => !!filterValue);
};

const getActiveFilterObj = (
  filters: Array<FilterObject>,
  filterKey: string,
  type: LINK_FILTER_TYPE | MULTISELECT_FILTER_TYPE
): ActiveFilters | null => {
  const filterOn = filterKey.split(':')[0];
  const activeFilter = filters.find((filter: FilterObject) => filter.filterOn === filterOn);

  return activeFilter
    ? {
        [filterOn as string]: {
          filterType: type,
          queryParamKey: activeFilter.queryParamKey,
          filterOnName: activeFilter.name,
          filterContentType: activeFilter.filterContentType,
          values: activeFilter.values
            .filter((value: FilterValueObject) => value.active)
            .map((value: FilterValueObject) => ({
              filterKey: value.filterKey,
              filterKeyName: value.name,
            })),
        },
      }
    : null;
};

export const generateActiveFiltersFormCategoryAndType = (
  filters: Array<FilterObject>,
  category?: string,
  type?: Array<string>
): FilterState => {
  const filterState: FilterState = {};

  if (category) {
    filterState.activeFilters = {
      ...getActiveFilterObj(filters, category, LINK_FILTER),
    };
  }

  if (type && type.length > 0) {
    filterState.activeFilters = {
      ...filterState.activeFilters,
      ...getActiveFilterObj(filters, type[0]!, MULTISELECT_FILTER),
    };
  }

  return filterState;
};

/**
 * Removes items from an array (`collection`) if they satisfy a given condition
 * when compared against any item in another array (`criteria`).
 * @param collection - collection to filter
 * @param criteria - collection to compare against
 * @param condition to remove collection
 *
 * @returns A new array containing elements from `collection` that do not satisfy
 * the condition for any element in `criteria`.
 */
export const subtractIf = <T, U>(
  collection: Array<T>,
  criteria: Array<U>,
  condition: (item: T, criterion: U) => boolean
): Array<T> => {
  return collection.filter(item => !criteria.some(criterion => condition(item, criterion)));
};
